import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import classes from './SizeSlider.module.scss';

interface Props extends React.HTMLAttributes<HTMLElement> {
    sizes: number[];
    activeStep: number;
    onSetActiveStep: (step: number) => void;
}

function SizeSlider({ sizes, onSetActiveStep, activeStep }: Props) {
    const { t } = useTranslation();

    /**
   * Ref
   */
    const thumbRef = useRef<HTMLDivElement>(null);
    const thumbShadowRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const stepWrapper = useRef<HTMLDivElement>(null);

    /**
   * Slider Related
   */
    const [thumbWidth, setThumbWidth] = useState(32);

    /**
   * Window state
   */
    const [mouseCoordinates, setMouseCoordinates] = useState({ x: 0, y: 0 });
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

    /**
   * Thumb state
   */
    const [sliderThumbInset, setSliderThumbInset] = useState<number>(0);
    const [sliderThumbPosition, setSliderThumbPosition] = useState<number>(0);
    const [sliderThumbSelected, setSliderThumbSelected] = useState<boolean>(false);
    const [sliderThumbIsMoving, setSliderThumbIsMoving] = useState<boolean>(false);
    const [thumbTransition, setThumbTransition] = useState<string>('none');

    /**
   * Step state
   */
    const [stepXPositions, setStepXPositions] = useState<number[]>([]);

    const stepCount = sizes.length;

    /**
   * Measures the x position of each step and the offset of the track.
   * Runs on mount, on resize and when the number of steps changes.
   */
    const measureSteps = useCallback(() => {
        const stepElements = stepWrapper.current?.children;

        if (!stepElements || stepElements.length === 0) {
            setStepXPositions([]);
            return;
        }

        setStepXPositions(
            Array.from(stepElements).map((ch) => ch.getBoundingClientRect().left),
        );
        setSliderThumbInset(trackRef.current?.getBoundingClientRect().left || 0);
    }, []);

    /**
   * Keeps a step index inside the range of the current steps
   */
    const clampStep = useCallback(
        (step: number) => Math.min(Math.max(step, 0), Math.max(stepXPositions.length - 1, 0)),
        [stepXPositions.length],
    );

    /**
   * General mouse event handler to track and set the position of the mouse
   * @param event
   */
    const pointerMoveHandler = (event: PointerEvent) => {
        setMouseCoordinates({
            x: event.clientX,
            y: event.clientY,
        });
    };

    const toggleThumbTransition = () => {
        if (!sliderThumbSelected) {
            setThumbTransition('all 0.4s ease');
            setTimeout(() => {
                setThumbTransition('none');
            }, 300);
        }
    };

    const thumbPos = useCallback(() => {
        if (stepXPositions.length === 0) {
            return 0;
        }

        const minPos = stepXPositions[0];
        const maxPos = stepXPositions[stepXPositions.length - 1];

        if (mouseCoordinates.x < minPos) {
            return 0 - thumbWidth / 2;
        } if (mouseCoordinates.x > maxPos) {
            // Ensure we can reach the last step by using the exact maxPos
            return maxPos - sliderThumbInset - thumbWidth / 2;
        }
        return mouseCoordinates.x - sliderThumbInset - thumbWidth / 2;
    }, [
        mouseCoordinates.x,
        sliderThumbInset,
        stepXPositions,
        thumbWidth,
    ]);

    const closestStep = useMemo(() => {
        if (stepXPositions.length === 0) {
            return 0;
        }

        const minPos = stepXPositions[0];
        const maxPos = stepXPositions[stepXPositions.length - 1];
        const currentThumbPos = thumbPos();
        const thumbCenterX = sliderThumbInset + currentThumbPos + thumbWidth / 2;

        // Handle edge cases: if thumb is at or beyond the last step, return last index
        if (thumbCenterX >= maxPos) {
            return stepXPositions.length - 1;
        }
        // Handle edge case: if thumb is before the first step, return first index
        if (thumbCenterX <= minPos) {
            return 0;
        }

        // Find the closest step
        const closest = [...stepXPositions].sort((a: number, b: number) => Math.abs(a - thumbCenterX) - Math.abs(b - thumbCenterX));
        return stepXPositions.indexOf(closest[0]);
    }, [stepXPositions, thumbPos, sliderThumbInset, thumbWidth]);

    const onTrackClick = (
        event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    ) => {
        if (event.type === 'touchstart' || event.type === 'touchend') {
            const e = event as React.TouchEvent<HTMLDivElement>;
            const touch = e.touches[0] || e.changedTouches[0];

            if (touch) {
                setMouseCoordinates({
                    x: touch.clientX,
                    y: touch.clientY,
                });
            }

            if (e.type === 'touchstart') {
                if (touch) {
                    setSliderThumbSelected(true);
                    setSliderThumbPosition(
                        touch.clientX - sliderThumbInset - thumbWidth / 2,
                    );
                }
            } else if (e.type === 'touchend') {
                // Update coordinates one last time before releasing
                if (touch) {
                    setMouseCoordinates({
                        x: touch.clientX,
                        y: touch.clientY,
                    });
                }
                setSliderThumbSelected(false);
            }
        } else if (event.type === 'mousedown' || event.type === 'mouseup') {
            const e = event as React.MouseEvent<HTMLDivElement>;
            if (e.type === 'mousedown' && !sliderThumbSelected) {
                setSliderThumbPosition(e.clientX - sliderThumbInset - thumbWidth / 2);
                setSliderThumbSelected(true);
            } else if (e.type === 'mouseup' && sliderThumbSelected) {
                setSliderThumbSelected(false);
            }
        }
    };

    useEffect(() => {
        window.addEventListener('resize', () => {
            const wSize = { width: window.innerWidth, height: window.innerHeight };
            if (!(windowSize === wSize)) {
                setWindowSize({ width: window.innerWidth, height: window.innerHeight });
            }
        });

        window.addEventListener('pointermove', pointerMoveHandler);
        window.addEventListener('pointerup', () => {
            if (sliderThumbSelected) {
                setSliderThumbSelected(false);
            }
        });
        return () => {
            window.addEventListener('resize', () => {
                const wSize = { width: window.innerWidth, height: window.innerHeight };
                if (!(windowSize === wSize)) {
                    setWindowSize({
                        width: window.innerWidth,
                        height: window.innerHeight,
                    });
                }
            });

            window.addEventListener('pointermove', pointerMoveHandler);
            window.addEventListener('pointerup', () => {
                if (sliderThumbSelected) {
                    setSliderThumbSelected(false);
                }
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!sliderThumbSelected && stepXPositions.length > 0) {
            toggleThumbTransition();
            setSliderThumbPosition(
                stepXPositions[clampStep(activeStep)] - sliderThumbInset - thumbWidth / 2,
            );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, sliderThumbSelected, stepXPositions]);

    useEffect(() => {
        measureSteps();
    }, [measureSteps, windowSize, stepCount]);

    useEffect(() => {
        if (sliderThumbSelected && stepXPositions.length > 0) {
            onSetActiveStep(closestStep);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closestStep, sliderThumbSelected]);

    useEffect(() => {
        if (sliderThumbSelected) {
            setSliderThumbPosition(thumbPos());
            if (!sliderThumbIsMoving) {
                setSliderThumbIsMoving(true);
            }
        } else if (sliderThumbIsMoving) {
            toggleThumbTransition();
            if (stepXPositions.length > 0) {
                onSetActiveStep(closestStep);
                setSliderThumbPosition(
                    stepXPositions[clampStep(closestStep)] - sliderThumbInset - thumbWidth / 2,
                );
            }
            setSliderThumbIsMoving(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliderThumbSelected, mouseCoordinates.x]);

    useEffect(() => {
        if (thumbRef.current) {
            setThumbWidth(thumbRef.current.getBoundingClientRect().width);
        }
    }, []);

    return (
        <div className={`${classes.wrapper}`} data-testid="sizeslider">
            <div
                className={`${classes.track}`}
                ref={trackRef}
                onMouseDown={onTrackClick}
                onMouseUp={onTrackClick}
                onTouchStart={onTrackClick}
                onTouchEnd={onTrackClick}
            >
                <div
                    className={`${classes.thumb}`}
                    ref={thumbRef}
                    onPointerDown={() => setSliderThumbSelected(true)}
                    onPointerUp={() => setSliderThumbSelected(false)}
                    style={{
                        left: `${sliderThumbPosition}px`,
                        transition: `${thumbTransition}`,
                    }}
                    draggable="false"
                />
                <div className={`${classes.track_container}`}>
                    <div
                        className={`${classes.thumb_shadow}`}
                        ref={thumbShadowRef}
                        style={{
                            left: `${sliderThumbPosition + 12}px`,
                            transition: `${thumbTransition}`,
                        }}
                    />
                </div>
                <div className={`${classes.step_wrapper}`} ref={stepWrapper}>
                    {sizes.map((size: number, index: number) => (
                        <div
                            key={`${index}_step_idx`}
                            id={`step_${index}`}
                            className={`${classes.step}`}
                        />
                    ))}
                </div>
            </div>

            <div className={classes.labels}>
                <span>{t('sizeSlider:small')}</span>
                <span>{t('sizeSlider:medium')}</span>
                <span>{t('sizeSlider:large')}</span>
            </div>
        </div>
    );
}

export default SizeSlider;
